'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { parseImagePosition } from '@/utils/parseImagePosition';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Animation helpers ------------------------------ */

function normalizeMediaUrl(input) {
    if (!input) return '';
    if (typeof input === 'string') return input.trim();
    if (typeof input === 'object') {
        const candidate =
            input.Media || input.media || input.url || input.src || input.value;
        return typeof candidate === 'string' ? candidate.trim() : '';
    }
    return '';
}

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        [
            'media',
            'media-only',
            'media only',
            'medias',
            'image',
            'images',
            'img',
            'background',
            'bg',
        ].includes(v)
    )
        return 'media-only';
    if (['all', 'both', 'content+media', 'content-media'].includes(v))
        return 'all';
    if (
        ['cta', 'ctas', 'cta-only', 'ctas-only', 'buttons', 'button'].includes(
            v
        )
    )
        return 'ctas-only';
    return 'content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

/* ------------------------------ Alignment & spacing ------------------------------ */

function getAlignmentClasses(alignment) {
    switch (String(alignment || 'center').toLowerCase()) {
        case 'center':
            return { textAlign: 'text-center', buttonAlign: 'justify-center' };
        case 'left':
            return { textAlign: 'text-left', buttonAlign: 'justify-start' };
        case 'right':
            return { textAlign: 'text-right', buttonAlign: 'justify-end' };
        default:
            return { textAlign: 'text-left', buttonAlign: 'justify-center' };
    }
}

// centralized (non-responsive) CTA spacing
function getCTASpacing(size) {
    switch (String(size || 'normal').toLowerCase()) {
        case 'tight':
            return 'mt-6';
        case 'loose':
            return 'mt-12';
        case 'normal':
        default:
            return 'mt-10';
    }
}

/* -------------------------------- Component -------------------------------- */

export default function HeroHalf(props) {
    const {
        Header,
        Body,
        ImageUrl,
        AltText,

        // CTAs (rendered via getCTA)
        CTA,
        CustomCTALink,
        SecondaryButtonText,
        SecondaryButtonLink,
        OutlineButtonText,
        OutlineButtonLink,
        ImagePosition = 'left',
        ImageObjectPosition = '50',

        ColorScheme = 'light',
        VerticalSpacing = 'medium',

        // animation controls
        Animation = 'fade', // 'none' | 'fade' | 'slide' | 'zoom' | 'stagger'
        Duration = 0.6,
        AnimationGroup = 'content', // 'content' | 'media-only' | 'ctas-only'
        EditorPreview = false,

        // layout controls
        Alignment = 'center', // centers CTAs; text remains left by default
        CTASpacing = 'normal', // 'tight' | 'normal' | 'loose'
    } = props;

    const imageSrc = normalizeMediaUrl(ImageUrl) || '/images/placeholder.svg';

    const {
        backgroundColor,
        textColor,
        headerColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const ctaVSpace = getCTASpacing(CTASpacing);
    const { textAlign, buttonAlign } = getAlignmentClasses(Alignment);
    const hasImage = Boolean(imageSrc);
    const imagePosRaw =
        typeof ImagePosition === 'object' && ImagePosition !== null
            ? ImagePosition.value ?? ImagePosition
            : ImagePosition;
    const imageSide = String(imagePosRaw || 'left').toLowerCase();
    const imageOnRight = imageSide === 'right';
    const mediaOrderClass = imageOnRight ? 'lg:order-2' : 'lg:order-1';
    const textOrderClass = imageOnRight ? 'lg:order-1' : 'lg:order-2';
    const { x: imagePosX, y: imagePosY } =
        parseImagePosition(ImageObjectPosition);
    const imageObjectPosition = `${imagePosX}% ${imagePosY}%`;

    /* ------------------------------ Animation state ------------------------------ */

    const preset = normalizeAnimation(Animation);
    const group = mapGroup(AnimationGroup);

    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    const animateContent = group === 'content' || group === 'all';
    const animateMedia = group === 'media-only' || group === 'all';
    const animateCTAs =
        group === 'ctas-only' || group === 'all' || group === 'content';

    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);

    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `hero-half-${group}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    // TEXT container (stagger only)
    const textContainerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger || !animateContent) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, animateContent, Duration, EditorPreview]);

    // TEXT children
    const textChildMotionProps = useMemo(() => {
        if (!enableAnim || !animateContent) return {};
        if (isStagger) {
            return {
                variants: {
                    hidden: { opacity: 0, y: 16 },
                    show: {
                        opacity: 1,
                        y: 0,
                        transition: {
                            duration: Math.max(
                                0.4,
                                Number(Duration) - 0.2 || 0.2
                            ),
                        },
                    },
                },
            };
        }
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [
        enableAnim,
        animateContent,
        isStagger,
        preset,
        Duration,
        EditorPreview,
    ]);

    // MEDIA (image) animation — its own group
    const mediaMotionProps = useMemo(() => {
        if (!enableAnim || !animateMedia) return {};
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, animateMedia, preset, Duration, EditorPreview]);

    // CTAs container animation (independent group)
    const ctaContainerMotionProps = useMemo(() => {
        if (!enableAnim || !animateCTAs || !hasAnyCTA) return {};
        if (preset === 'stagger') {
            const variants = {
                hidden: {},
                show: {
                    transition: {
                        when: 'beforeChildren',
                        staggerChildren: 0.08,
                    },
                },
            };
            return EditorPreview
                ? { variants, initial: 'hidden', animate: 'show' }
                : {
                      variants,
                      initial: 'hidden',
                      whileInView: 'show',
                      viewport,
                  };
        }
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, animateCTAs, hasAnyCTA, preset, Duration, EditorPreview]);

    // children animation for getCTA buttons (used by the util)
    const ctaChildMotionProps = useMemo(() => {
        if (!enableAnim || !animateCTAs) return {};
        if (preset === 'stagger') {
            return {
                variants: {
                    hidden: { opacity: 0, y: 12 },
                    show: {
                        opacity: 1,
                        y: 0,
                        transition: {
                            duration: Math.max(
                                0.3,
                                Number(Duration) - 0.2 || 0.2
                            ),
                        },
                    },
                },
            };
        }
        return {}; // inherit from container for non-stagger
    }, [enableAnim, animateCTAs, preset, Duration]);

    // Build CTAs via getCTA
    const { hasAny, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign, // centers the CTA row when Alignment='center'
            enableAnim: enableAnim && animateCTAs,
            motionProps: ctaChildMotionProps, // per-button motion for stagger
        }
    );

    /* -------------------------------- Render -------------------------------- */

    return (
        <div
            key={baseKey}
            className={`editable-component relative ${backgroundColor}`}
            style={backgroundStyle}
            data-json="calltoaction"
        >
            <div
                className={`grid grid-cols-1 ${
                    hasImage ? 'lg:grid-cols-2' : ''
                }`}
            >
                {/* MEDIA (image/video) — its own animation group */}
                {hasImage && (
                    <MotionMaybe
                        enable={enableAnim && animateMedia}
                        k={`${baseKey}-media`}
                        motionProps={mediaMotionProps}
                        className={mediaOrderClass}
                    >
                        <img
                            className="h-[350px] w-full object-cover md:h-[450px] lg:h-full lg:min-h-[20vw]"
                            src={imageSrc}
                            alt={AltText || ''}
                            width={1000}
                            height={800}
                            style={{ objectPosition: imageObjectPosition }}
                        />
                    </MotionMaybe>
                )}

                {/* TEXT + CTAs column */}
                <div
                    className={`relative z-30 mx-auto max-w-2xl flex-col px-6 md:max-w-3xl md:px-12 lg:flex lg:justify-center lg:px-16 ${vSpace} ${textOrderClass}`}
                >
                    {/* TEXT block — animates only when AnimationGroup='content' */}
                    <MotionMaybe
                        enable={enableAnim && isStagger && animateContent}
                        k={`${baseKey}-text-container`}
                        motionProps={textContainerMotionProps}
                        className="max-w-xl lg:max-w-2xl"
                    >
                        {Header && (
                            <MotionMaybe
                                enable={enableAnim && animateContent}
                                k={`${baseKey}-header`}
                                motionProps={textChildMotionProps}
                            >
                                <MarkDown
                                    variant="h2"
                                    className={`w-full ${textAlign} font-display text-4xl tracking-tight ${headerColor} lg:max-w-6xl lg:text-5xl xl:text-6xl`}
                                >
                                    {Header}
                                </MarkDown>
                            </MotionMaybe>
                        )}

                        {Body && (
                            <MotionMaybe
                                enable={enableAnim && animateContent}
                                k={`${baseKey}-body`}
                                motionProps={textChildMotionProps}
                            >
                                <MarkDown
                                    className={`mt-6 w-full ${textAlign} text-base font-text leading-7 md:text-lg ${textColor}`}
                                >
                                    {Body}
                                </MarkDown>
                            </MotionMaybe>
                        )}
                    </MotionMaybe>

                    {/* CTAs — centered, centralized spacing, independent animation via getCTA */}
                    {hasAny && (
                        <MotionMaybe
                            enable={enableAnim && animateCTAs}
                            k={`${baseKey}-ctas`}
                            motionProps={ctaContainerMotionProps}
                            className={`flex w-full flex-wrap ${buttonAlign} gap-4 ${ctaVSpace}`}
                        >
                            {CTAs}
                        </MotionMaybe>
                    )}
                </div>
            </div>
        </div>
    );
}
